/**
 * Renders the Insights Summary share snapshot to a 1080 × 1080 PNG blob.
 *
 * Implementation: the layout lives in `<InsightsSnapshot>` (CSS modules,
 * Popcorn primitives, real brand fonts). At capture time we mount it into
 * an offscreen DOM container, wait for fonts + the avatar image to be
 * ready, then use `html-to-image` to serialize the DOM (via SVG
 * `foreignObject`) into a PNG. Replaces the legacy canvas-drawing path so
 * design changes are made in JSX + CSS instead of by tweaking canvas math.
 *
 * The exported API (`captureSummaryAsBlob`, `CaptureSummaryOptions`,
 * `SummaryStat`) is preserved so callers like `useExportSummaryAsPost`
 * don't need to change.
 */

import { createRoot } from 'react-dom/client'
import { flushSync } from 'react-dom'
import { toBlob } from 'html-to-image'

import { InsightsSnapshot } from './components/InsightsSnapshot'
import type {
  InsightsSnapshotProps,
  SummaryStat as SnapshotStat,
} from './components/InsightsSnapshot'
import { loadSnapshotFontEmbedCSS } from './snapshotFontEmbedCSS'

export type SummaryStat = SnapshotStat
export type CaptureSummaryOptions = InsightsSnapshotProps

const CANVAS_SIZE = 1080
const AVATAR_PROXY_BASE = 'https://images.buffer.com/proxy/280x280/'

/**
 * Routes the channel avatar URL through Buffer's image proxy so html-to-image
 * gets a CORS-safe response. Most social-network CDNs (`media.licdn.com`,
 * `scontent.cdninstagram.com`, …) don't send CORS headers for our origin and
 * would taint the captured canvas; the Buffer proxy does, and is the same
 * proxy other image-heavy surfaces in publish-frontend already use.
 */
export function buildAvatarProxyUrl(
  src: string | undefined,
): string | undefined {
  if (!src) return undefined
  if (src.startsWith(AVATAR_PROXY_BASE)) return src
  return `${AVATAR_PROXY_BASE}${src}`
}

/**
 * Resolves once every `<img>` inside the container has finished loading (or
 * failed). Without this, html-to-image can serialize the DOM while the
 * avatar is still streaming and produce a blank tile.
 */
function waitForImages(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll('img'))
  if (imgs.length === 0) return Promise.resolve()
  return Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve()
      return new Promise<void>((resolve) => {
        img.addEventListener('load', () => resolve(), { once: true })
        img.addEventListener('error', () => resolve(), { once: true })
      })
    }),
  ).then(() => undefined)
}

/**
 * Renders the snapshot React tree into a hidden offscreen container,
 * captures it to PNG via html-to-image, and tears the container down.
 */
export async function captureSummaryAsBlob(
  options: CaptureSummaryOptions,
): Promise<{ blob: Blob; width: number; height: number }> {
  const container = document.createElement('div')
  // Position offscreen but in-flow so all CSS — including layout, web
  // fonts and CSS custom properties — resolves exactly as it would for a
  // visible element. `pointerEvents:none` keeps the hidden node from
  // intercepting clicks while it lives in the DOM.
  const containerStyle: Partial<CSSStyleDeclaration> = {
    height: `${CANVAS_SIZE}px`,
    left: '-99999px',
    pointerEvents: 'none',
    position: 'fixed',
    top: '0',
    width: `${CANVAS_SIZE}px`,
    zIndex: '-1',
  }
  Object.assign(container.style, containerStyle)
  document.body.appendChild(container)

  const root = createRoot(container)
  try {
    flushSync(() => {
      root.render(
        <InsightsSnapshot
          {...options}
          channelAvatarUrl={buildAvatarProxyUrl(options.channelAvatarUrl)}
        />,
      )
    })

    // Make sure the page's brand fonts (Stolzl / Inter) have finished
    // loading before serializing — otherwise html-to-image bakes in the
    // fallback system font.
    if (typeof document.fonts?.ready?.then === 'function') {
      await document.fonts.ready
    }
    await waitForImages(container)

    const target = container.firstElementChild
    if (!(target instanceof HTMLElement)) {
      throw new Error('Insights snapshot failed to mount')
    }

    // No `backgroundColor` option — the snapshot's root element has
    // `background: var(--insights-snapshot-bg)` in CSS, so html-to-image's
    // canvas-fallback colour is redundant and would just duplicate the
    // hex outside the CSS module.
    //
    // `fontEmbedCSS` short-circuits html-to-image's default font discovery,
    // which throws `SecurityError` on cross-origin stylesheets (Google
    // Fonts). When the pre-computed CSS is unavailable, fall back to
    // `skipFonts` so we still avoid the discovery path — see CORE-5602.
    const fontEmbedCSS = await loadSnapshotFontEmbedCSS()
    const blob = await toBlob(target, {
      cacheBust: false,
      height: CANVAS_SIZE,
      pixelRatio: 1,
      width: CANVAS_SIZE,
      ...(fontEmbedCSS != null ? { fontEmbedCSS } : { skipFonts: true }),
    })
    if (!blob) throw new Error('Failed to capture snapshot blob')

    return { blob, width: CANVAS_SIZE, height: CANVAS_SIZE }
  } finally {
    root.unmount()
    container.remove()
  }
}
